/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

export interface CachedDesignImage {
  id: string;
  base64: string;
  timestamp: number;
  uploaded?: boolean;
  uploadedImageId?: string;
  uploadedFilepath?: string;
}

export class IndexedDBService {
  private dbName = 'veritas_designs';
  private storeName = 'thumbnails';
  private designStoreName = 'design_images';
  private version = 2; // Increment version to add new store

  private async getDB(): Promise<IDBDatabase> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(this.dbName, this.version);

      request.onupgradeneeded = (event) => {
        const db = (event.target as IDBOpenDBRequest).result;

        // Legacy thumbnail store
        if (!db.objectStoreNames.contains(this.storeName)) {
          db.createObjectStore(this.storeName);
        }

        // New design images store with metadata
        if (!db.objectStoreNames.contains(this.designStoreName)) {
          const store = db.createObjectStore(this.designStoreName, { keyPath: 'id' });
          store.createIndex('timestamp', 'timestamp', { unique: false });
          store.createIndex('uploaded', 'uploaded', { unique: false });
          console.log('[IndexedDB] Created design_images store');
        }
      };

      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  }

  // ===== LEGACY THUMBNAIL METHODS =====
  async saveThumbnail(orderId: string, imageData: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.put(imageData, orderId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  async getThumbnail(orderId: string): Promise<string | null> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readonly');
      const store = transaction.objectStore(this.storeName);
      const request = store.get(orderId);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async deleteThumbnail(orderId: string): Promise<void> {
    const db = await this.getDB();
    return new Promise((resolve, reject) => {
      const transaction = db.transaction(this.storeName, 'readwrite');
      const store = transaction.objectStore(this.storeName);
      const request = store.delete(orderId);

      request.onsuccess = () => resolve();
      request.onerror = () => reject(request.error);
    });
  }

  // ===== NEW DESIGN IMAGE METHODS =====

  /**
   * Save design image to IndexedDB (lazy upload optimization)
   */
  async saveDesignImage(id: string, base64: string): Promise<void> {
    const startTime = performance.now();

    try {
      const db = await this.getDB();
      const transaction = db.transaction([this.designStoreName], 'readwrite');
      const store = transaction.objectStore(this.designStoreName);

      const data: CachedDesignImage = {
        id,
        base64,
        timestamp: Date.now(),
        uploaded: false
      };

      await new Promise<void>((resolve, reject) => {
        const request = store.put(data);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      const duration = performance.now() - startTime;
      console.log(`[IndexedDB] ✅ Saved design image in ${duration.toFixed(2)}ms`, {
        id: id.substring(0, 8) + '...',
        size: `${(base64.length / 1024).toFixed(2)}KB`
      });
    } catch (error) {
      console.error('[IndexedDB] ❌ Failed to save design image:', error);
      throw error;
    }
  }

  /**
   * Get design image from IndexedDB
   */
  async getDesignImage(id: string): Promise<CachedDesignImage | null> {
    const startTime = performance.now();

    try {
      const db = await this.getDB();
      const transaction = db.transaction([this.designStoreName], 'readonly');
      const store = transaction.objectStore(this.designStoreName);

      const data = await new Promise<CachedDesignImage | undefined>((resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      const duration = performance.now() - startTime;

      if (data) {
        console.log(`[IndexedDB] ✅ Retrieved design image in ${duration.toFixed(2)}ms`, {
          id: id.substring(0, 8) + '...',
          uploaded: data.uploaded
        });
        return data;
      } else {
        console.log(`[IndexedDB] ⚠️ Design image not found: ${id.substring(0, 8)}...`);
        return null;
      }
    } catch (error) {
      console.error('[IndexedDB] ❌ Failed to get design image:', error);
      return null;
    }
  }

  /**
   * Mark image as uploaded and store Modal metadata
   */
  async markAsUploaded(
    id: string,
    uploadedImageId: string,
    uploadedFilepath: string
  ): Promise<void> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction([this.designStoreName], 'readwrite');
      const store = transaction.objectStore(this.designStoreName);

      const existing = await new Promise<CachedDesignImage | undefined>((resolve, reject) => {
        const request = store.get(id);
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(request.error);
      });

      if (!existing) {
        console.warn(`[IndexedDB] Cannot mark non-existent image as uploaded: ${id}`);
        return;
      }

      const updated: CachedDesignImage = {
        ...existing,
        uploaded: true,
        uploadedImageId,
        uploadedFilepath
      };

      await new Promise<void>((resolve, reject) => {
        const request = store.put(updated);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      console.log(`[IndexedDB] ✅ Marked as uploaded:`, {
        id: id.substring(0, 8) + '...',
        uploadedImageId: uploadedImageId.substring(0, 8) + '...'
      });
    } catch (error) {
      console.error('[IndexedDB] ❌ Failed to mark as uploaded:', error);
      throw error;
    }
  }

  /**
   * Delete design image from IndexedDB
   */
  async deleteDesignImage(id: string): Promise<void> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction([this.designStoreName], 'readwrite');
      const store = transaction.objectStore(this.designStoreName);

      await new Promise<void>((resolve, reject) => {
        const request = store.delete(id);
        request.onsuccess = () => resolve();
        request.onerror = () => reject(request.error);
      });

      console.log(`[IndexedDB] ✅ Deleted design image: ${id.substring(0, 8)}...`);
    } catch (error) {
      console.error('[IndexedDB] ❌ Failed to delete design image:', error);
      throw error;
    }
  }

  /**
   * Clear old cached images (older than 24 hours)
   */
  async clearOldCache(): Promise<void> {
    try {
      const db = await this.getDB();
      const transaction = db.transaction([this.designStoreName], 'readwrite');
      const store = transaction.objectStore(this.designStoreName);
      const index = store.index('timestamp');

      const cutoffTime = Date.now() - (24 * 60 * 60 * 1000); // 24 hours
      const range = IDBKeyRange.upperBound(cutoffTime);

      const request = index.openCursor(range);
      let deletedCount = 0;

      await new Promise<void>((resolve, reject) => {
        request.onsuccess = (event) => {
          const cursor = (event.target as IDBRequest).result;
          if (cursor) {
            cursor.delete();
            deletedCount++;
            cursor.continue();
          } else {
            resolve();
          }
        };
        request.onerror = () => reject(request.error);
      });

      if (deletedCount > 0) {
        console.log(`[IndexedDB] ✅ Cleared ${deletedCount} old cached images`);
      }
    } catch (error) {
      console.error('[IndexedDB] ❌ Failed to clear old cache:', error);
    }
  }
}

export const indexedDBService = new IndexedDBService();
